import { useState, useEffect } from 'react';
import { NewsCategory } from '../types';

interface CategoryModalProps {
  category: NewsCategory | null;
  onSave: (category: Omit<NewsCategory, 'id'> & { id?: string }) => Promise<void>;
  onClose: () => void;
}

export default function CategoryModal({ category, onSave, onClose }: CategoryModalProps) {
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [keywords, setKeywords] = useState('');
  const [keywordsBlacklist, setKeywordsBlacklist] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (category) {
      setName(category.name || '');
      setIsActive(category.isActive !== false);
      setKeywords((category.keywords || []).join(', '));
      setKeywordsBlacklist((category.keywordsBlacklist || []).join(', '));
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Category name is required');
      return;
    }

    // Parse keywords from comma-separated string
    const keywordsArray = keywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    const keywordsBlacklistArray = keywordsBlacklist
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    setSaving(true);
    try {
      await onSave({
        ...(category ? { id: category.id } : {}),
        name: name.trim(),
        isActive,
        keywords: keywordsArray.length > 0 ? keywordsArray : undefined,
        keywordsBlacklist: keywordsBlacklistArray.length > 0 ? keywordsBlacklistArray : undefined,
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to save category');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal modal-blur fade show"
      style={{ display: 'block' }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{category ? 'Edit Category' : 'Add Category'}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="row mb-3">
                <div>
                  <label className="form-label required">Category Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g., Florestal, Agricultura, Ambiente"
                    required
                  />
                  <small className="form-hint">ID will be auto-generated from the name.</small>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                  />
                  <span className="form-check-label">Active</span>
                </label>
              </div>

              <div className="mb-3">
                <label className="form-label">Keywords (for auto-categorization)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                  placeholder="e.g., floresta, florestal, árvore, madeira, eucalipto, pinheiro"
                />
                <small className="form-hint">
                  Comma-separated keywords. Articles matching these will be auto-categorized.
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Exclude Keywords (blacklist)</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={keywordsBlacklist}
                  onChange={e => setKeywordsBlacklist(e.target.value)}
                  placeholder="e.g., apoio psicológico, seguro de saúde"
                />
                <small className="form-hint">
                  Comma-separated keywords. Articles matching any of these will NOT be assigned this
                  category, even if a whitelist keyword matched.
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  'Save Category'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
